/**
 * Table Sorting Utilities
 *
 * This module provides clean, reusable utilities for handling table column sorting.
 * It supports both client-side and server-side sorting patterns used throughout the app.
 */

import type { SorterResult } from "antd/es/table/interface";

// Constants for sort order
export const ASCENDING = "asc";
export const DESCENDING = "desc";

export type SortOrder = typeof ASCENDING | typeof DESCENDING;

/**
 * Maps Ant Design's sorter order strings to our internal format
 *
 * @param antdOrder - The order string from Ant Design ('ascend', 'descend', or undefined)
 * @returns Our internal sort order format ('asc', 'desc', or null)
 */
export function mapAntdSortOrder(
	antdOrder: "ascend" | "descend" | undefined | null,
): SortOrder | null {
	if (antdOrder === "ascend") return ASCENDING;
	if (antdOrder === "descend") return DESCENDING;
	return null;
}

/**
 * Maps our internal sort order format to Ant Design's expected format
 *
 * @param sortOrder - Our internal sort order ('asc' or 'desc')
 * @returns Ant Design's sort order format ('ascend' or 'descend')
 */
export function mapToAntdSortOrder(sortOrder: SortOrder): "ascend" | "descend" {
	return sortOrder === ASCENDING ? "ascend" : "descend";
}

/**
 * Adds sort order information to a column definition for controlled sorting
 * This ensures Ant Design displays the correct sort arrow on column headers
 *
 * IMPORTANT: This function removes defaultSortOrder and sortOrder from all columns,
 * then adds sortOrder only to the active column. This ensures proper controlled sorting.
 *
 * @param columns - Array of column definitions
 * @param sortBy - The current field being sorted by
 * @param sortOrder - The current sort order ('asc' or 'desc')
 * @returns Column array with sortOrder properties set correctly
 */
export function addSortOrderToColumns(
	columns: any[],
	sortBy: string,
	sortOrder: SortOrder,
): any[] {
	return columns.map((col) => {
		// Remove both sortOrder and defaultSortOrder to ensure clean state
		const {
			sortOrder: _removedSort,
			defaultSortOrder: _removedDefault,
			...cleanCol
		} = col;

		// Only add sortOrder if this column has sorting enabled and is the active sort column
		if (cleanCol.sorter && cleanCol.key === sortBy) {
			return {
				...cleanCol,
				sortOrder: mapToAntdSortOrder(sortOrder),
			};
		}

		// Return column without any sort order properties
		return cleanCol;
	});
}

/**
 * Generic sort handler for Ant Design table onChange events
 * Extracts sorting information and dispatches appropriate actions
 *
 * IMPORTANT: Uses the column's 'key' field for sorting, not the 'field' (dataIndex)
 * This ensures nested properties like ["manufacturer", "name"] work correctly,
 * as the backend expects keys like "manufacturer" not the nested path.
 *
 * @param sorter - The sorter result from Ant Design's onChange
 * @param dispatch - The dispatch function from useReducer
 * @returns Object with sortBy and sortOrder, or null if no sorting
 */
export function handleTableSort<T>(
	sorter: SorterResult<T> | SorterResult<T>[],
	dispatch: (action: any) => void,
): { sortBy: string; sortOrder: SortOrder } | null {
	// Handle array of sorters (multi-column sort) by taking the first one
	const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

	// Only process if we have both columnKey/field and order
	if (singleSorter?.order) {
		// Prefer columnKey over field, as columnKey is explicitly set and field might be an array (dataIndex)
		// For nested properties like ["manufacturer", "name"], we need the key "manufacturer", not the array
		const sortByField =
			(singleSorter.columnKey as string) || (singleSorter.field as string);

		if (sortByField) {
			const sortOrder = mapAntdSortOrder(singleSorter.order);

			if (sortOrder) {
				// Dispatch the sorting state updates
				dispatch({ type: "SET_SORT_BY", payload: sortByField });
				dispatch({ type: "SET_SORT_ORDER", payload: sortOrder });

				return { sortBy: sortByField, sortOrder };
			}
		}
	}

	return null;
}

/**
 * Helper function to get a nested property value from an object
 * Handles both simple keys ("name") and nested paths ("user.name" or accessing via dot notation)
 *
 * @param obj - The object to get the value from
 * @param path - The property path (e.g., "name" or "user.name")
 * @returns The value at the path, or undefined if not found
 */
function getNestedValue(obj: any, path: string): any {
	// Handle simple property access first (most common case)
	if (obj && obj[path] !== undefined) {
		return obj[path];
	}

	// Handle dot notation paths (e.g., "user.name")
	if (path.includes(".")) {
		return path.split(".").reduce((current, key) => current?.[key], obj);
	}

	// Return the direct property or undefined
	return obj?.[path];
}

/**
 * Client-side sorting function for local data
 * Sorts an array of objects by a specified field and order
 * Handles nested properties by checking common patterns (e.g., if sortBy is "role", tries "role.name")
 *
 * @param data - Array of objects to sort
 * @param sortBy - The field name to sort by (can be simple like "name" or nested like "role")
 * @param sortOrder - The sort order ('asc' or 'desc')
 * @returns Sorted array (does not mutate original)
 */
export function sortDataClientSide<T extends Record<string, any>>(
	data: T[],
	sortBy: string,
	sortOrder: SortOrder,
): T[] {
	if (!sortBy || !data || data.length === 0) {
		return data;
	}

	// Create a copy to avoid mutating the original array
	const sortedData = [...data];

	sortedData.sort((a, b) => {
		// Try to get the value directly first
		let aVal = getNestedValue(a, sortBy);
		let bVal = getNestedValue(b, sortBy);

		// If the value is an object (nested property), try accessing common sub-properties
		// This handles cases like sortBy="role" where the actual value is at "role.name"
		if (
			aVal &&
			typeof aVal === "object" &&
			!Array.isArray(aVal) &&
			!(aVal instanceof Date)
		) {
			// Try common property names for nested objects
			aVal = aVal.name || aVal.value || aVal.id || aVal.label || String(aVal);
		}
		if (
			bVal &&
			typeof bVal === "object" &&
			!Array.isArray(bVal) &&
			!(bVal instanceof Date)
		) {
			bVal = bVal.name || bVal.value || bVal.id || bVal.label || String(bVal);
		}

		// Handle null/undefined values - always push them to the end
		if (aVal == null && bVal == null) return 0;
		if (aVal == null) return 1;
		if (bVal == null) return -1;

		// String comparison (case-insensitive)
		if (typeof aVal === "string" && typeof bVal === "string") {
			const comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
			return sortOrder === ASCENDING ? comparison : -comparison;
		}

		// Numeric comparison
		if (typeof aVal === "number" && typeof bVal === "number") {
			return sortOrder === ASCENDING ? aVal - bVal : bVal - aVal;
		}

		// Date comparison
		if (aVal instanceof Date && bVal instanceof Date) {
			return sortOrder === ASCENDING
				? aVal.getTime() - bVal.getTime()
				: bVal.getTime() - aVal.getTime();
		}

		// Default comparison using < and >
		if (aVal < bVal) {
			return sortOrder === ASCENDING ? -1 : 1;
		}
		if (aVal > bVal) {
			return sortOrder === ASCENDING ? 1 : -1;
		}

		return 0;
	});

	return sortedData;
}

/**
 * Creates a complete onChange handler for Ant Design tables
 * Handles pagination, filters, and sorting in one function
 *
 * @param currentState - The current table state
 * @param dispatch - The dispatch function
 * @returns An onChange handler function for the table
 */
export function createTableChangeHandler(
	currentState: {
		pageNum: number;
		pageSize: number;
	},
	dispatch: (action: any) => void,
) {
	return (pagination: any, filters: any, sorter: any) => {
		// Handle pagination changes
		if (
			pagination.current !== undefined &&
			pagination.current !== currentState.pageNum
		) {
			dispatch({ type: "SET_PAGE_NUM", payload: pagination.current });
		}
		if (
			pagination.pageSize !== undefined &&
			pagination.pageSize !== currentState.pageSize
		) {
			dispatch({ type: "SET_PAGE_SIZE", payload: pagination.pageSize });
		}

		// Handle filter changes
		if (filters) {
			// Clean up null filters
			const cleanedFilters = Object.fromEntries(
				Object.entries(filters).filter(([_, value]) => value !== null),
			);
			dispatch({ type: "SET_FILTERS", payload: cleanedFilters });
		}

		// Handle sorting
		handleTableSort(sorter, dispatch);
	};
}
